import {
  Card,
  CardContent,
  CardHeader,
  Grid2,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';

import { RoleName } from '@constants/role_name';

import { useListUserAccessedByPermissions } from '@hooks/api/access-permission';
import { useGetAccount, useGetAccountUsers } from '@hooks/api/account';
import { useSession } from '@hooks/useSession';

import { formatName } from '@utils/format';
import { isDependant, isPatient } from '@utils/role';
import { sortProfiles } from '@utils/sort/profiles';

const messages = defineMessages({
  noPermissions: {
    id: 'clientProfileCard.noPermission',
    defaultMessage: 'No Permissions',
  },
  connections: {
    id: 'clientProfileCard.connections',
    defaultMessage: 'Plan Members',
  },
  noMembers: {
    id: 'clientProfileCard.noMembers',
    defaultMessage: 'No plan members',
  },
  accessAllowed: {
    id: 'viewingPermissions.accessAllowed',
    defaultMessage: 'Allowed Access',
  },
  accessNotAllowed: {
    id: 'viewingPermissions.accessNotAllowed',
    defaultMessage: 'No Access',
  },
  dependant: {
    id: 'GLOBAL.dependant',
    defaultMessage: 'Dependant',
  },
  primaryUser: {
    id: 'GLOBAL.primaryUser',
    defaultMessage: 'Primary User',
  },
  patient: {
    id: 'GLOBAL.patient',
    defaultMessage: 'Patient',
  },
  nonPatient: {
    id: 'GLOBAL.nonPatient',
    defaultMessage: 'Non-Patient',
  },
});

interface AccountMembersCardProps {
  user_id: string;
}

const AccountMembersCard: React.FC<AccountMembersCardProps> = ({ user_id }) => {
  const { formatMessage } = useIntl();
  const {
    state: { active_user_id, activeProfile },
  } = useSession();

  const { palette } = useTheme();

  const { data: usersData, loading: usersLoading } = useGetAccountUsers();
  const { data: accountData, loading: accountLoading } = useGetAccount();
  const sortedUsers = sortProfiles(usersData ?? [], user_id);
  const { data: accessByUsers, loading: listUserAccessedByLoading } =
    useListUserAccessedByPermissions(user_id);

  const isAccountAdmin =
    activeProfile?.roles?.some(
      ({ role_name }) => role_name === RoleName.AccountAdmin
    ) ?? false;

  const loading = usersLoading || listUserAccessedByLoading || accountLoading;

  return (
    <Card>
      <ContentLoader loading={loading}>
        <CardHeader
          subheader={
            sortedUsers.length === 0 && formatMessage(messages.noMembers)
          }
          title={formatMessage(messages.connections)}
          slotProps={{
            title: {
              sx: {
                color: (theme) => theme.palette.primary.main,
              },
            },
          }}
        />
        {sortedUsers.length > 0 && (
          <CardContent>
            <Grid2 container spacing={3}>
              {sortedUsers.map((user) => {
                const { first_name, last_name, user_id } = user;
                // Primary user who is a patient should have full access to themselves
                const isPrimaryPatientUser =
                  active_user_id === user_id &&
                  isAccountAdmin &&
                  activeProfile?.vitall_patient_id;

                const hasPermission =
                  isPrimaryPatientUser ||
                  accessByUsers.some(({ user_id }) => user_id === user.user_id);

                const isOwner =
                  accountData?.getAccountRecord.owner_id === user_id;
                const label = [
                  ...(isOwner ? [formatMessage(messages.primaryUser)] : []),
                  ...[
                    isPatient(user)
                      ? `${
                          isDependant(user)
                            ? formatMessage(messages.dependant)
                            : ''
                        } ${formatMessage(messages.patient)}`
                      : formatMessage(messages.nonPatient),
                  ],
                ].join(', ');

                return (
                  <Grid2
                    key={`client-profile-account-user-${user_id}`}
                    size={{ xs: 12, sm: 3 }}
                  >
                    <Typography color="primary.main">
                      {formatName(first_name, last_name) ?? '-'}
                    </Typography>
                    <Typography
                      color={palette.text.secondary}
                      variant="subtitle2"
                    >
                      {label}
                    </Typography>
                    <Typography color="text.primary">
                      {formatMessage(
                        hasPermission
                          ? messages.accessAllowed
                          : messages.accessNotAllowed
                      )}
                    </Typography>
                  </Grid2>
                );
              })}
            </Grid2>
          </CardContent>
        )}
      </ContentLoader>
    </Card>
  );
};

export default AccountMembersCard;
